import { IPrintService, IPrintServiceDelegates, IPrintServiceInjectable } from './interface';
import i18n from '@i18next/i18n';
import { Snackbar } from '@tencent/dui/lib/components/Snackbar';
import { getQuery } from 'sutils';
import { convertPxToCm } from '@src/slide/infrastructure/module/independent/unit';
import { UIState } from '@src/slide/infrastructure/module/independent/ui-state';
import ua from '@tencent/docs-user-agent';
import axios from 'axios';
import { SlideDataServices } from '@src/slide/domain/slide-app/core/slide-data-services.core';
import * as mobx from 'mobx';
import { IBaseCopyPaste } from '../../copy-paste/core/base/base-copy-paste.interface';
import { browser } from '@src/utils/agent';
import { sendToDeviceToPrint } from '@src/js/common/ark-extensions/dc-components/send-to-pc';
import eventBus, { EventKeys } from '@src/slide/infrastructure/module/independent/event-emitter/instance';

const PRINT_FRAME_ID = 'print_frame';
/** slide转图片进度占总进度比例 */
const SLIDE2IMG_PROGRESS_RATE = 0.8;
/** 保存图片进度占总进度比例 */
const SAVE_IMG_PROGRESS_RATE = 0.2;

enum FILE_FORMAT {
    BASE64 = 'base64',
    ARRAY_BUFFER = 'arrayBuffer'
}

export class PrintService implements IPrintService {
    @mobx.observable
    public isPrintOnMobile = false;
    public copyPasteManager?: IBaseCopyPaste;
    private services: IPrintServiceDelegates;
    private loadingBar: ReturnType<typeof Snackbar.show>;
    private isPrinting: boolean;
    private printIframe: HTMLIFrameElement | null;
    private jsPDFLoader;
    private serviceLoadPromise: Promise<void>;
    private copyPasteLoadPromise: Promise<void>;
    private resolveServiceLoad: (value?: void | PromiseLike<void>) => void;
    private resolveCopyPasteLoad: (value?: void | PromiseLike<void>) => void;

    public constructor(private injectable?: IPrintServiceInjectable) {
        this.init();
        this.loadJsPDF();
    }

    public setServiceDelegates(services: Partial<IPrintServiceDelegates>) {
        this.services = {
            ...this.services,
            ...services,
        };
        this.resolveServiceLoad();
    }

    public setCopyPasteManager(copyPasteManager: IBaseCopyPaste) {
        this.copyPasteManager = copyPasteManager;
        this.resolveCopyPasteLoad();
    }

    public handlePrint = async (skipCheck = false) => {
        if (this.isPrinting) return;
        if (this.injectable?.shouldSendToDeviceToPrint) {
            this.sendToDeviceToPrint();
            return;
        }
        if (!skipCheck && (this.shouldHidePrint() || this.shouldDisablePrint())) return;
        try {
            this.openLoadingSnackBar();
            if (this.injectable?.shouldInvokeExternalPrintOnLegacy) {
                this.injectable?.invokeExternalPrint();
                return;
            }
            this.isPrinting = true;
            Promise.race([this.doPrint(), this.cancelPrint()]);
        } catch (error) {
            this.disposeResources();
            if (this.injectable?.shouldInvokeExternalLoading) return;
            Snackbar.show({
                message: i18n.t('打印失败'),
                type: 'error',
                autoClose: true,
            });
            console.error('print error', error);
        }
    };

    public shouldHidePrint(): boolean {
        return !!this.injectable?.shouldHidePrint;
    }

    public shouldDisablePrint(): boolean {
        return !!this.injectable?.shouldDisablePrint;
    }

    private init() {
        this.serviceLoadPromise = new Promise<void>((resolve) => {
            this.resolveServiceLoad = resolve;
        });
        this.copyPasteLoadPromise = new Promise<void>((resolve) => {
            this.resolveCopyPasteLoad = resolve;
        });
        this.invokePrintWhenLoad();
    }

    private loadJsPDF() {
        this.jsPDFLoader = import('jspdf');
    }

    private sendToDeviceToPrint() {
        sendToDeviceToPrint();
    }

    private async cancelPrint() {
        const res = await this.injectable?.waitForCancel();
        if (res) {
            const exportPDFManager = await this.getExportPdfManager();
            this.disposeResources();
            exportPDFManager.getInstance().cancelExport();
        }
    }

    private async doPrint() {
        if (this.injectable?.shouldInvokeNewPrintOnApp) {
            this.printOnMobileApp();
            return;
        }
        // 如果唤起端上外部打印，则返回pdf的url或者ArrayBuffer
        const shouldInvokeExternalPrint = await this.injectable?.checkShouldInvokeExternalPrint();
        if (shouldInvokeExternalPrint) {
            if (this.injectable?.pdfNotNeededWhenExternalPrint) {
                this.injectable?.invokeExternalPrint();
            } else {
                const pdf = await this.getPrintPdf();
                this.injectable?.invokeExternalPrint(pdf);
            }
            this.disposeResources();
            return;
        }
        // 否则唤起浏览器打印
        this.printInBrowser();
    }

    private async getPrintPdf() {
        if (this.injectable?.shouldOfflinePrint) {
            return await this.getLocalPdf('arraybuffer');
        }
        const res = await this.getExportPdf();
        return res;
    }

    private openLoadingSnackBar() {
        if (this.injectable?.shouldInvokeExternalLoading) return;
        this.loadingBar = Snackbar.show({
            message: i18n.t('准备打印中，请稍等。'),
            type: 'loading',
            autoClose: false,
        });
    }

    private closeLoadingSnackBar() {
        this.loadingBar.close();
    }

    /**
     * 获取pdf导出服务的url
     * @returns
     */
    private async getExportPdf(): Promise<string> {
        const exportPDFManager = await this.getExportPdfManager();
        return new Promise((resolve, reject) => {
            exportPDFManager.getInstance().localExport({
                shouldShowTips: false,
                onFinish: (
                    error: { message?: string; code?: number } | null,
                    data?: { url?: string; parentId?: string; folderId?: string; localPadID?: string },
                ) => {
                    if (data?.url) {
                        resolve(data.url);
                    } else {
                        reject(error?.message ||  i18n.t('导出PDF失败'));
                    }
                },
            });
        });
    }

    /**
     * 获取前端生成的pdf文件 blob url或者Arraybuffer
     * @returns
     */
    private async getLocalPdf(returnType: 'bloburl' | 'arraybuffer' = 'bloburl'): Promise<string | ArrayBuffer> {
        const imageList = await this.getSlideImageList();
        const { slideWidth, slideHeight } = this.services.uiDataService.presentation.props;
        const slideWidthCm = Number(convertPxToCm(slideWidth).toFixed(2));
        const slideHeightCm = Number(convertPxToCm(slideHeight).toFixed(2));
        const { jsPDF } = await this.jsPDFLoader;
        const jsPdf = new jsPDF({
            orientation: 'landscape',
            unit: 'cm',
            format: [slideWidthCm, slideHeightCm],
        });
        jsPdf.deletePage(1);
        imageList.forEach((image, index) => {
            jsPdf.addPage();
            jsPdf.addImage(image, 'PNG', 0, 0, slideWidthCm, slideHeightCm);
            imageList[index] = null;
        });
        const pdf = jsPdf.output(returnType);
        return pdf;
    }

    private async getSlideImageList(fileFormat?: FILE_FORMAT) {
        let showingSlides = this.services.uiDataService.presentation.children.filter(slide => slide.isShowInPlay);
        const slidesLength = showingSlides.length;
        if (slidesLength === 0) {
            // 全部隐藏，默认视为全部不隐藏
            showingSlides = this.services.uiDataService.presentation.children;
        }
        const copyPaste = this.copyPasteManager;
        const imageList: any[] = [];
        UIState.isExportingToImage = true;
        for (let i = 0; i < slidesLength; i++) {
            // getSlideDataToCanvas并行会导致slide画布元素偏移
            const canvas = await copyPaste!.getSlideDataToCanvas([showingSlides[i]], 1) as HTMLCanvasElement;
            if (fileFormat === FILE_FORMAT.ARRAY_BUFFER) {
                const arrayBuffer = await new Promise(resolve => canvas.toBlob((blob) => {
                    resolve(blob?.arrayBuffer());
                }));
                imageList.push(arrayBuffer);
            } else {
                imageList.push(canvas.toDataURL());
            }
            // 释放canvas内存
            canvas.height = 0;
            canvas.width = 0;
            if (this.injectable?.shouldInvokeNewPrintOnApp) {
                const progress = (i + 1) / slidesLength * SLIDE2IMG_PROGRESS_RATE;
                this.updatePrintLoadingProgress(Math.round(progress * 100));
            }
        }
        UIState.isExportingToImage = false;
        return imageList;
    }

    private printInBrowser = async () => {
        const printIframe = this.createIFrame();
        this.printInIframe(printIframe);
    };

    private createIFrame() {
        const lastPrintIframe = document.getElementById(PRINT_FRAME_ID);
        if (lastPrintIframe) {
            lastPrintIframe.parentNode?.removeChild(lastPrintIframe);
        }
        const printIframe = document.createElement('iframe');
        printIframe.setAttribute('style', 'visibility: hidden; position: absolute; border: 0;');
        printIframe.setAttribute('id', PRINT_FRAME_ID);
        this.printIframe = printIframe;
        return printIframe;
    }

    private getIframeDoc() {
        return this.printIframe
            && (this.printIframe.contentDocument || (this.printIframe.contentWindow?.document));
    }

    // 插入打印stylesheet
    private insertPrintStyleSheet(styleContent, document) {
        const mediaStyle = document.createElement('style');
        mediaStyle.type = 'text/css';
        mediaStyle.media = 'print';
        try {
            mediaStyle.appendChild(document.createTextNode(styleContent));
        } catch (e) {
            (mediaStyle as any).cssText = styleContent;
        }
        const headNode = document.getElementsByTagName('head')[0];
        if (headNode) {
            headNode.appendChild(mediaStyle);
        }
    }

    private setPageSize(document) {
        const styleContent = `
        @page {
            size: A4 landscape;
            margin: 0;
        }
        body {
            margin: 0;
        }
        .print-page {
            height: 100%;
            width: 100%;
            page-break-after: always;
            box-sizing: border-box;
            display: flex;
            align-items: center;
            justify-content: center;
        }
        .print-page:last-child {
            page-break-after: avoid;
        }
        .print-img {
            max-width: 100%;
            max-height: 100%;
        }
        `;
        this.insertPrintStyleSheet(styleContent, document);
    }

    private printInIframe = async (printIframe) => {
        document.body.appendChild(printIframe);
        const imageList = await this.getSlideImageList();
        const iframeDoc = this.getIframeDoc();
        if (iframeDoc) {
            this.setPageSize(iframeDoc);
            const container = iframeDoc.createElement('div');
            for (let i = 0; i < imageList.length; i++) {
                const pageDom = iframeDoc.createElement('div');
                pageDom.classList.add('print-page');
                const image = imageList[i];
                const img = iframeDoc.createElement('img');
                img.classList.add('print-img');
                img.src = image;
                pageDom.appendChild(img);
                container.appendChild(pageDom);
            }
            iframeDoc.body.appendChild(container);
        }
        setTimeout(() => {
            printIframe.focus();
            printIframe.contentWindow?.print();
            this.disposeResources();
        }, 0);
    };

    private disposeResources = () => {
        this.isPrinting = false;
        this.printIframe = null;
        this.closeLoadingSnackBar();
    };

    private async getExportPdfManager() {
        return (await import('@tencent/docs-scenario-components-export-pdf')).ExportPDFManager;
    }

    private async invokePrintWhenLoad() {
        const print = getQuery('print');
        if (print === '1') {
            if (this.injectable?.shouldInvokeNewPrintOnApp) {
                // 如果是移动端 需要页面加载完以后触发isPrintOnMobile触发，触发编辑层挂载逻辑
                await new Promise((resolve) => {
                    eventBus.onIncludingPast(EventKeys.ALL_THUMBNAIL_SITE_COMPLETE, () => {
                        resolve(true);
                    });
                });
                this.isPrintOnMobile = true;
            }
            Promise.all([this.serviceLoadPromise, this.copyPasteLoadPromise]).then(() => {
                this.handlePrint(true);
            });
        }
    }

    /**
     * 打印改用前端生成pdf给app，并调起打印
     */
    private async printOnMobileApp() {
        this.isPrintOnMobile = true;
        const imageList = await this.getSlideImageList(FILE_FORMAT.ARRAY_BUFFER);
        const localServer = await this.getLocalServer();
        const imageFileNameList: string[] = [];
        let finishNum = 0;
        const res = await Promise.all(imageList.map((image, index) => {
            const fileName = `img_${index}.png`;
            imageFileNameList.push(fileName);
            return this.saveData2TempFile(localServer, fileName, image).then((res) => {
                if (res) {
                    finishNum += 1;
                    const progress = (finishNum / imageList.length * SAVE_IMG_PROGRESS_RATE) + SLIDE2IMG_PROGRESS_RATE;
                    this.updatePrintLoadingProgress(Math.round(progress * 100));
                }
                return res;
            });
        }));
        if (res.length && res.every(item => item)) {
            const fileName = `${SlideDataServices.SlideTitleManager()?.title}${browser.isAndroid ? '.pdf' : ''}`;
            this.printImageFile(fileName, imageFileNameList);
        }
    }

    private getLocalServer(): Promise<String> {
        return new Promise<string>((resolve, reject) => {
            try {
                window.mqq.invoke('docx', 'getLocalServer', {}, (result) => {
                    resolve(result.data);
                });
            } catch (error) {
                reject(error);
            }
        });
    }

    private async saveData2TempFile(localServer, fileName, data) {
        const url = `${ua.isAndroid ? 'http://' : 'https://'}${localServer}/local-edit/saveData2TempFile?fileName=${fileName}&bodyIsBuffer=1`;
        const result = await axios({
            method: 'POST',
            url,
            data,
        });
        const { code, message } = result.data;
        if (code !== 0) {
            throw new Error(`saveData2TempFile failed, message:${message}, code:${code}`);
        }
        return true;
    }

    private async printImageFile(fileName, imageFileNameList) {
        return new Promise<boolean>((resolve, reject) => {
            try {
                window.mqq.invoke(
                    'docx',
                    'printImageFile',
                    {
                        pdfFileName: fileName,
                        printConfig: {
                            orientation: 'landscape',
                        },
                        imageFileNameList,
                    },
                    (result) => {
                        if (result.code === 0) {
                            resolve(true);
                        } else {
                            throw new Error(`printImageFile failed, message:${result.message || result.msg}, code:${result.code}`);
                        }
                        this.disposeResources();
                    },
                );
            } catch (error) {
                this.disposeResources();
                throw new Error('printImageFile failed', error);
            }
        });
    }

    private updatePrintLoadingProgress(progress: number) {
        window.mqq.invoke('docx', 'updatePrintLoadingProgress', { progress });
    }
}
